import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, Box, Tabs, Tab,
  TextField, Button, Avatar, Grid, Divider,
  Switch, FormControlLabel, Alert, CircularProgress
} from '@mui/material';
import { ArrowBack, Save, Lock } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../services/api';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ p: 3 }}>{children}</Box> : null;
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState(location.state?.tab || 0);
  const [user, setUser] = useState({});
  const [profileMsg, setProfileMsg] = useState({ type: '', text: '' });
  const [pwdMsg, setPwdMsg] = useState({ type: '', text: '' });
  const [profileLoading, setProfileLoading] = useState(false);
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwd, setPwd] = useState({ current: '', newPwd: '', confirm: '' });

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem('era_user') || '{}');
    setUser(stored);
    authService.getCurrentUser()
      .then(r => { setUser(r.data.user); localStorage.setItem('era_user', JSON.stringify(r.data.user)); })
      .catch(() => {});
  }, []);

  const handleSaveProfile = async () => {
    setProfileLoading(true);
    setProfileMsg({ type: '', text: '' });
    try {
      await authService.updateProfile({ name: user.name });
      localStorage.setItem('era_user', JSON.stringify(user));
      setProfileMsg({ type: 'success', text: 'Profil mis a jour avec succes !' });
    } catch (err) {
      setProfileMsg({ type: 'error', text: err.response?.data?.error || 'Erreur lors de la mise a jour.' });
    } finally {
      setProfileLoading(false);
      setTimeout(() => setProfileMsg({ type: '', text: '' }), 4000);
    }
  };

  const handleChangePassword = async () => {
    setPwdMsg({ type: '', text: '' });
    if (!pwd.current || !pwd.newPwd || !pwd.confirm)
      return setPwdMsg({ type: 'error', text: 'Veuillez remplir tous les champs.' });
    if (pwd.newPwd !== pwd.confirm)
      return setPwdMsg({ type: 'error', text: 'Les nouveaux mots de passe ne correspondent pas.' });
    if (pwd.newPwd.length < 6)
      return setPwdMsg({ type: 'error', text: 'Le nouveau mot de passe doit contenir au moins 6 caracteres.' });
    setPwdLoading(true);
    try {
      await authService.changePassword({ currentPassword: pwd.current, newPassword: pwd.newPwd });
      setPwdMsg({ type: 'success', text: 'Mot de passe modifie avec succes !' });
      setPwd({ current: '', newPwd: '', confirm: '' });
    } catch (err) {
      setPwdMsg({ type: 'error', text: err.response?.data?.error || 'Erreur lors du changement de mot de passe.' });
    } finally {
      setPwdLoading(false);
      setTimeout(() => setPwdMsg({ type: '', text: '' }), 4000);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/dashboard')} sx={{ mb: 2 }}>
        Retour au tableau de bord
      </Button>
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ p: 3, bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center' }}>
          <Avatar sx={{ width: 80, height: 80, mr: 3, border: '4px solid white', bgcolor: 'secondary.main', fontSize: '2rem' }}>
            {user.name?.charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight="bold">{user.name}</Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>{user.email}</Typography>
            {user.role === 'admin' && (
              <Typography variant="caption" sx={{ bgcolor: 'rgba(255,255,255,0.2)', px: 1, py: 0.3, borderRadius: 1 }}>
                Administrateur
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            <Tab label="Informations personnelles" />
            <Tab label="Securite" />
            <Tab label="Visibilite" />
          </Tabs>
        </Box>

        <TabPanel value={tab} index={0}>
          <Typography variant="h6" gutterBottom>Modifier le profil</Typography>
          {profileMsg.text && <Alert severity={profileMsg.type} sx={{ mb: 2 }}>{profileMsg.text}</Alert>}
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Nom complet" value={user.name || ''}
                onChange={(e) => setUser({ ...user, name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Email" value={user.email || ''} disabled
                helperText="L'adresse email ne peut pas etre modifiee" />
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained"
                startIcon={profileLoading ? <CircularProgress size={16} color="inherit" /> : <Save />}
                onClick={handleSaveProfile} disabled={profileLoading}>
                Enregistrer
              </Button>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tab} index={1}>
          <Typography variant="h6" gutterBottom>Changer le mot de passe</Typography>
          {pwdMsg.text && <Alert severity={pwdMsg.type} sx={{ mb: 2 }}>{pwdMsg.text}</Alert>}
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth type="password" label="Mot de passe actuel"
                value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="password" label="Nouveau mot de passe"
                value={pwd.newPwd} onChange={(e) => setPwd({ ...pwd, newPwd: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="password" label="Confirmer le nouveau mot de passe"
                value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" color="secondary"
                startIcon={pwdLoading ? <CircularProgress size={16} color="inherit" /> : <Lock />}
                onClick={handleChangePassword} disabled={pwdLoading}>
                Changer le mot de passe
              </Button>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tab} index={2}>
          <Typography variant="h6" gutterBottom>Parametres de visibilite</Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Gerez qui peut voir votre profil et vos activites sur ERA.
          </Typography>
          <Box sx={{ mt: 2 }}>
            <FormControlLabel control={<Switch defaultChecked />} label="Rendre mon profil public" />
            <Typography variant="caption" display="block" color="text.secondary" sx={{ ml: 7, mb: 2 }}>
              Si desactive, seuls les membres de vos projets pourront voir votre profil.
            </Typography>
            <FormControlLabel control={<Switch defaultChecked />} label="Afficher mon email" />
            <Divider sx={{ my: 2 }} />
            <FormControlLabel control={<Switch defaultChecked />} label="Statut en ligne" />
          </Box>
        </TabPanel>
      </Paper>
    </Container>
  );
}
